import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, Terminal } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const isDev = import.meta.env.DEV;

interface AuthPageProps {
  mode?: 'login' | 'signup';
}

export function Login({ mode = 'login' }: AuthPageProps) {
  const { isAuthenticated, isLoading, login, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [devLoading, setDevLoading] = useState(false);

  const isSignup = mode === 'signup';

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const getErrorMessage = (error: string | null): string | null => {
    switch (error) {
      case 'oauth_denied':
        return 'You cancelled the sign-in process. Please try again.';
      case 'invalid_state':
        return 'Invalid authentication state. Please try again.';
      case 'no_code':
        return 'Authentication failed. Please try again.';
      case 'unverified_email':
        return 'Please verify your Google email address first.';
      case 'auth_failed':
        return 'Authentication failed. Please try again.';
      default:
        return null;
    }
  };

  const errorMessage = getErrorMessage(error);

  const handleDevLogin = async () => {
    setDevLoading(true);
    try {
      const response = await fetch('/api/auth/dev-login', {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        await refresh();
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Dev login failed:', err);
    } finally {
      setDevLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <img src="/logo.png" alt="SitemapHost" className="h-10 w-auto" />
            <span className="text-2xl font-bold">SitemapHost</span>
          </div>
          <p className="text-slate-600">Hosted XML Sitemaps for Headless Sites</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-2xl shadow-xl border p-8">
          <h1 className="text-2xl font-bold text-center mb-2">
            {isSignup ? 'Get Started' : 'Welcome Back'}
          </h1>
          <p className="text-slate-600 text-center mb-6">
            {isSignup
              ? 'Create your free account to start hosting sitemaps'
              : 'Sign in to manage your sitemaps'}
          </p>

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errorMessage}
            </div>
          )}

          {/* Google Auth Button */}
          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-slate-200 rounded-xl font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            <GoogleIcon />
            {isSignup ? 'Sign up with Google' : 'Continue with Google'}
          </button>

          {/* Dev Login Button (only in development) */}
          {isDev && (
            <button
              onClick={handleDevLogin}
              disabled={devLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 mt-3 bg-amber-100 border-2 border-amber-300 rounded-xl font-medium hover:bg-amber-200 transition-colors text-amber-800"
            >
              <Terminal className="w-5 h-5" />
              {devLoading ? 'Logging in...' : 'Dev Login (Local Only)'}
            </button>
          )}

          {/* Terms */}
          <p className="text-xs text-slate-500 text-center mt-6">
            By {isSignup ? 'signing up' : 'signing in'}, you agree to our{' '}
            <a href="/terms" className="text-primary hover:underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </a>
          </p>

          {/* Switch between login/signup */}
          <p className="text-sm text-slate-600 text-center mt-4">
            {isSignup ? (
              <>
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary font-medium hover:underline">
                  Sign up free
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Back to marketing site */}
        <p className="text-center mt-6 text-sm text-slate-500">
          <a href="https://www.sitemaphost.app" className="hover:text-slate-700">
            &larr; Back to SitemapHost.app
          </a>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
